import { useEffect, useMemo } from 'react'
import Loading from 'components/loading/Loading'
import { useDispatch, useSelector } from 'react-redux'
import NoneAnchor from 'components/NoneAnchor'
import { DashboardInfoTypes, OperationStatus } from 'features/Codes'
import NumberFormat from 'react-number-format'
import { getStatus } from 'features/lnb/AdAccountManager'
import {
  fetchDashboardInfoAccount,
  setTab,
} from 'features/dashboard/info/dashboardInfoSlice'
import WalletSpend from 'features/dashboard/info/WalletSpend'
import { isEmptyObject } from 'utils'

const AdAccountInfo = () => {
  const dispatch = useDispatch()
  const {
    id: adAccountId,
    status: adAccountStatus,
    adAccountType,
    advertiserCompanyName,
  } = useSelector(state => state.account.current)
  const {
    isLoading,
    tabs,
    account: operation,
    accountError,
  } = useSelector(state => state.dashboard.info)
  const type = DashboardInfoTypes.AD_ACCOUNT
  const tabName = useMemo(() => tabs[type], [tabs, type])
  const status = useMemo(() => getStatus(adAccountStatus), [adAccountStatus])
  const offStatus = useMemo(
    () => status.filter(item => item.name !== OperationStatus.ON.name),
    [status]
  )
  useEffect(() => {
    if (
      !isLoading &&
      tabName === 'operationInfoTab' &&
      accountError === undefined &&
      isEmptyObject(operation)
    ) {
      dispatch(fetchDashboardInfoAccount())
    }
  }, [dispatch, tabName, isLoading, operation, accountError])

  useEffect(
    () => () => {
      dispatch(
        setTab({
          type,
          value: 'priceInfoTab',
        })
      )
    },
    [dispatch, type]
  )

  return isLoading ? (
    <div style={{ height: 368 }}>
      <Loading />
    </div>
  ) : (
    <>
      <strong className="screen_out">정보보기 탭</strong>
      <ul className="tab_g6">
        <li className={tabName === 'basicInfoTab' ? 'on' : ''}>
          <NoneAnchor
            className="link_tab"
            onClick={() =>
              dispatch(
                setTab({
                  type,
                  value: 'basicInfoTab',
                })
              )
            }>
            기본 정보
          </NoneAnchor>
        </li>
        <li className={tabName === 'priceInfoTab' ? 'on' : ''}>
          <NoneAnchor
            className="link_tab"
            onClick={() =>
              dispatch(
                setTab({
                  type,
                  value: 'priceInfoTab',
                })
              )
            }>
            비용 정보
          </NoneAnchor>
        </li>
        <li className={tabName === 'operationInfoTab' ? 'on' : ''}>
          <NoneAnchor
            className="link_tab"
            onClick={() =>
              dispatch(
                setTab({
                  type,
                  value: 'operationInfoTab',
                })
              )
            }>
            운영 정보
          </NoneAnchor>
        </li>
      </ul>
      {tabName === 'basicInfoTab' && (
        <div className="tab_cont">
          <h4 className="screen_out">기본 정보</h4>
          <div className="infosum_dash">
            <table className="tbl_infosum">
              <caption className="hide_caption">
                기본 정보 데이터 테이블
              </caption>
              <colgroup>
                <col />
                <col />
              </colgroup>
              <tbody>
                <tr>
                  <th>
                    <div className="inner_cell">ID</div>
                  </th>
                  <td>
                    <div className="inner_cell">{adAccountId}</div>
                  </td>
                </tr>
                <tr>
                  <th>
                    <div className="inner_cell">상태</div>
                  </th>
                  <td>
                    <div className="inner_cell">
                      {offStatus.length > 0 ? (
                        <>운영 불가({offStatus[0].title})</>
                      ) : (
                        <>운영 가능</>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>
                    <div className="inner_cell">유형</div>
                  </th>
                  <td>
                    <div className="inner_cell">
                      {adAccountType === 'INDIVIDUAL' ? '개인' : '사업자'}
                    </div>
                  </td>
                </tr>
                {adAccountType === 'BUSINESS' && advertiserCompanyName && (
                  <tr>
                    <th>
                      <div className="inner_cell">사업자</div>
                    </th>
                    <td>
                      <div className="inner_cell">
                        <span className="txt_limit2">
                          {advertiserCompanyName}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {tabName === 'priceInfoTab' && <WalletSpend />}
      {tabName === 'operationInfoTab' && !isEmptyObject(operation) && (
        <div className="tab_cont">
          <h4 className="screen_out">운영 정보</h4>
          <div className="infosum_dash">
            <table className="tbl_infosum">
              <caption className="hide_caption">
                운영 정보 데이터 테이블
              </caption>
              <colgroup>
                <col />
                <col />
              </colgroup>
              <tbody>
                <tr>
                  <th>
                    <div className="inner_cell">캠페인</div>
                  </th>
                  <td>
                    <div className="inner_cell">
                      운영가능{' '}
                      {operation.inOperationCampaignCount ? (
                        <NumberFormat
                          value={operation.inOperationCampaignCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                      <br />
                      운영불가{' '}
                      {operation.notInOperationCampaignCount ? (
                        <NumberFormat
                          value={operation.notInOperationCampaignCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>
                    <div className="inner_cell">광고그룹</div>
                  </th>
                  <td>
                    <div className="inner_cell">
                      운영가능{' '}
                      {operation.inOperationAdGroupCount ? (
                        <NumberFormat
                          value={operation.inOperationAdGroupCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                      <br />
                      운영불가{' '}
                      {operation.notInOperationAdGroupCount ? (
                        <NumberFormat
                          value={operation.notInOperationAdGroupCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>
                    <div className="inner_cell">키워드</div>
                  </th>
                  <td>
                    <div className="inner_cell">
                      심사승인{' '}
                      {operation.approvedKeywordCount ? (
                        <NumberFormat
                          value={operation.approvedKeywordCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                      <br />
                      심사중{' '}
                      {operation.approvingKeywordCount ? (
                        <NumberFormat
                          value={operation.approvingKeywordCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                      <br />
                      심사보류{' '}
                      {operation.rejectedApprovalKeywordCount ? (
                        <NumberFormat
                          value={operation.rejectedApprovalKeywordCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th>
                    <div className="inner_cell">소재</div>
                  </th>
                  <td>
                    <div className="inner_cell">
                      심사승인{' '}
                      {operation.approvedCreativeCount ? (
                        <NumberFormat
                          value={operation.approvedCreativeCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                      <br />
                      심사중{' '}
                      {operation.approvingCreativeCount ? (
                        <NumberFormat
                          value={operation.approvingCreativeCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                      <br />
                      심사보류{' '}
                      {operation.rejectedApprovalCreativeCount ? (
                        <NumberFormat
                          value={operation.rejectedApprovalCreativeCount}
                          displayType="text"
                          thousandSeparator
                        />
                      ) : (
                        0
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}

export default AdAccountInfo
